<template>
    <div id="account-files" v-if="!this.$parent.loading" :data-unique-model="uniqueModel">
        <b-loading :is-full-page="false" :active.sync="loading" :can-cancel="false">
            <b-icon
                    icon="sync-alt"
                    custom-class="fa-spin">
            </b-icon>
            <p>Please wait while we find the account files...</p>
        </b-loading>

        <b-notification type="is-light" :closable="false"
                        v-if="hasSelectionDownloadPending"
                        v-for="downloadProgress in selectionDownloads"
                        :key="downloadProgress.id">
            <div class="zip-generation-status">
                <h6>
                    Griptiller is generating your download
                    <div class="percent-complete pull-right">
                        {{ getPercentComplete(downloadProgress.progress.percentage_complete) }}%
                    </div>
                </h6>

                <p class="is-size-7">
                    <strong>{{ downloadProgress.progress.status }}</strong>
                    <template v-if="getPercentComplete(downloadProgress.progress.percentage_complete) < 100">:
                        {{ downloadProgress.progress.files_processed }}
                        of {{ downloadProgress.progress.total_files }}
                    </template>
                </p>

                <div class="zip-progress-bar-wrapper m-t-15">
                    <progress-bar :value="getPercentComplete(downloadProgress.progress.percentage_complete)" />
                </div>
            </div>
        </b-notification>

        <div class="columns">
            <div class="column" :class="{'is-two-thirds': !showHeader}">
                <div v-if="!loading && userCan('view_account_files_datatable_actions', 'account_files')" class="files-selector">
                    <div class="files-selector-dropdown">
                        <b-checkbox v-model="selectedAll" v-on:input="selectDefaultAction" :disabled="!hasFiles" />
                        <b-dropdown ref="rowSelector">
                            <button slot="trigger">
                                <b-icon icon="angle-down" size="is-small"></b-icon>
                            </button>

                            <b-dropdown-item v-on:click="selectEveryFile">
                                All
                            </b-dropdown-item>

                            <b-dropdown-item v-on:click="selectAll">
                                Just this page
                            </b-dropdown-item>

                            <b-dropdown-item v-on:click="selectNone">
                                None
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                    <div class="files-selector-actions">
                        <b-dropdown v-if="filesSelected.length">
                            <button class="button is-text" slot="trigger" :disabled="!hasFileActions">
                                <span>Actions</span>
                                <b-icon icon="angle-down" size="is-small"></b-icon>
                            </button>

                            <template v-if="filesSelected.length">
                                    <div class="actions" v-if="userCan('download_all_files', 'account_files')">
                                        <b-dropdown-item v-on:click="performAction('archive', true)">
                                            Download Selected Documents (Organized)
                                        </b-dropdown-item>
                                        <b-dropdown-item v-on:click="performAction('archive', false)">
                                            Download Selected Documents (Unorganized)
                                        </b-dropdown-item>
                                        <b-dropdown-item v-on:click="downloadDocList">
                                            Download selected document list
                                        </b-dropdown-item>
                                    </div>

                                    <hr class="dropdown-divider" v-if="userCan('delete_account_files', 'account_files')">
                                <b-dropdown-item v-on:click="deleteRows" v-if="userCan('delete_account_files', 'account_files')">
                                    <span class="has-text-danger">Delete Selected</span>
                                </b-dropdown-item>
                            </template>
                        </b-dropdown>
                    </div>
                    <div class="files-selector-info" v-if="filesSelected.length">
                        <strong>{{ filesSelected.length }}</strong> out of <strong>{{ pagination.total | numberFormat }}</strong> files selected
                    </div>
                </div>
            </div>

            <div class="column" :class="{'is-three-fifths': showHeader}">
                <div class="tab-actions">
                    <b-field v-if="hasFiles && !loading && showHeader && userCan('view_account_files_datatable_search', 'account_files')">
                        <b-input v-model="fileSearch"
                                 class="file-search"
                                 :class="{'is-searching': fileSearch}"
                                 placeholder="Search files..."
                                 type="search"
                                 icon="search">
                        </b-input>

                        <button class="button is-text reset"
                                v-if="fileSearch"
                                v-on:click="clearSearch">
                            <b-icon icon="times-circle"></b-icon>
                        </button>
                    </b-field>

                    <b-button  v-if="!loading && userCan('upload_account_files', 'account_files') && showUploadButton"
                               type="is-warning"
                               icon-pack="fas"
                               class="has-icon"
                               icon-left="upload"
                               tag="router-link"
                               :to="uploadRoute">
                        Upload Files
                    </b-button>
                </div>
            </div>
        </div>

        <transition name="animateHeight" mode="out-in" duration="1000">
            <div class="active-filter-notice" v-if="hasFiles && !loading && fileSearch"
                 v-on:click="clearSearch()">
                <p>You're currently viewing filtered results, some data may not be visible. <span>Click here</span> to view all records.</p>
            </div>
        </transition>

        <div class="account-files account-files" :class="[{'no-properties': !showProperties}, {'no-added-on': !showAddedOn}, { 'no-file-editing': !userCan('update_account_files', 'account_files') }]" v-if="!loading">
            <b-message type="is-warning" v-if="!hasFiles && !loading">
                Files not found.
            </b-message>
            <b-message type="is-warning" v-if="hasFiles && fileSearch && files.length === 0">
                Files not found.
            </b-message>

            <b-loading :is-full-page="false" :active.sync="loadingPage" :can-cancel="false" />

            <template v-if="hasFiles && files.length > 0">
                <div class="files-header" :class="{'no-details': !showFileDetails}">
                    <div class="files-type">&nbsp;</div>
                    <div class="files-name sortable" v-on:click="sort(showVanityByDefault ? 'vanity_name' : 'files.original_file_name')">
                        {{ showVanityByDefault ? 'Vanity File Name' : 'File Name' }}
                        <div class="files-sort-icon" v-if="sortCol === 'files.original_file_name' || sortCol === 'vanity_name'">
                            <b-icon
                                    pack="fa"
                                    icon="long-arrow-alt-up"
                                    :class="(sortDir === 'asc') ? 'is-visible' : 'is-hidden'" />
                            <b-icon
                                    pack="fa"
                                    icon="long-arrow-alt-down"
                                    :class="(sortDir === 'desc') ? 'is-visible' : 'is-hidden'" />
                        </div>
                    </div>
                    <div class="files-properties" v-if="showProperties">Properties</div>
                    <div class="files-report-date">
                        Report Date
                    </div>
                    <div class="files-date sortable" v-if="showAddedOn" v-on:click="sort('created_at')">
                        Added On
                        <div class="files-sort-icon" v-if="sortCol === 'created_at'">
                            <b-icon
                                    pack="fa"
                                    icon="long-arrow-alt-up"
                                    :class="(sortDir === 'asc') ? 'is-visible' : 'is-hidden'" />
                        </div>
                    </div>
                    <div class="files-who">Added By</div>
                    <div class="files-actions">
                        <button v-on:click="resetSorting" v-if="sortCol !== 'created_at'" class="resetSort" title="Reset Sort Selection">
                            <b-icon icon="redo" pack="fas" size="small" class="fa-flip-horizontal" />
                        </button>
                    </div>
                </div>
                <div class="files-wrapper" :class="[{'m-l-40': userCan('view_account_files_datatable_actions', 'account_files')}, {'no-details': !showFileDetails}]">
                    <div class="files-file" v-for="(file, index) in files" :key="`file-${file.id}`">
                        <div class="file-row">
                            <b-checkbox v-if="userCan('view_account_files_datatable_actions', 'account_files')"
                                        :disabled="!canSelectFile(file)"
                                        class="file-details-select"
                                        :value="file.id"
                                        :native-value="file.id"
                                        v-model="filesSelected" />
                            <b-button class="file-details-toggle"
                                      type="is-text"
                                      v-if="userCan('update_account_files', 'account_files') && file.accessible_features.update && showFileDetails"
                                      v-on:click="toggleFileDetails(file.id)">
                                <b-icon icon="chevron-down"
                                        :class="{'open': filesOpen.includes(file.id)}" />
                            </b-button>
                            <div class="files-row-wrapper" :class="{'is-private': files[index].private}">
                                <div class="files-type">
                                    <b-icon :icon="file.file.type" size="is-medium" :class="file.file.type" />
                                </div>
                                <div class="files-name" :class="{'full-name': showFullFileNames}">
                                    <template v-if="userCan('preview_files', 'account_files') && file.accessible_features.preview">
                                      <a :href="$router.resolve({ name: 'account-file', params: { file_id: file.id } }).href"
                                         title="Click to preview this file"
                                         @click.prevent="previewAccountFile(file)">
                                        <span>{{ showVanityByDefault ? file.vanity_name : file.file.original_file_name }}</span>
                                      </a>
                                    </template>
                                    <template v-else-if="userCan('download_files', 'account_files') && file.accessible_features.download">
                                      <a :title="showVanityByDefault ? `Download ${file.vanity_name}` : `Download ${file.file.original_file_name}`" href="#"
                                           v-on:click.prevent="getAccountFile(file)">
                                            <span>{{ showVanityByDefault ? file.vanity_name : file.file.original_file_name }}</span>
                                        </a>
                                    </template>
                                    <template v-else>
                                        <span>{{ showVanityByDefault ? file.vanity_name : file.file.original_file_name }}</span>
                                    </template>
                                </div>
                                <div class="files-properties" v-if="showProperties">
                                    <span>{{ file.properties.length }}</span>
                                </div>
                                <div class="files-report-date">
                                    <span v-if="file.report_author_date">{{ file.report_author_date }}</span>
                                    <span v-else>--</span>
                                </div>
                                <div class="files-date" v-if="showAddedOn">
                                    <span>{{ file.created_at }}</span>
                                </div>
                                <div class="files-who">
                                    <b-tooltip v-if="file.created_by" :label="file.created_by.email" animated>
                                        <span>{{ file.created_by.full_name }}</span>
                                        <a :href="`mailto:${file.created_by.email}`" title="Send Email">
                                            <b-icon icon="envelope" />
                                        </a>
                                    </b-tooltip>
                                  <div v-else><em>Unknown</em></div>
                                </div>
                                <div class="files-actions">
                                  <b-button class="strip inline-edit" size="is-small" :title="showVanityByDefault ? `Download ${file.vanity_name}` : `Download ${file.file.original_file_name}`" type="is-text"
                                              v-if="userCan('download_files', 'account_files') && file.accessible_features.download"
                                              v-on:click="getAccountFile(file)">
                                        <b-icon icon="download" />
                                    </b-button>
                                  <a v-if="userCan('preview_files', 'account_files') && file.accessible_features.preview"
                                       class="button is-text"
                                       :href="$router.resolve({ name: 'account-file', params: { file_id: file.id } }).href"
                                       title="Click to preview this file"
                                       @click.prevent="previewAccountFile(file)">
                                      <span><b-icon icon="eye"></b-icon></span>
                                  </a>
                                </div>
                            </div>
                        </div>
                        <div class="file-details" v-if="showFileDetails" :class="[{'open': filesOpen.includes(file.id)}, {'editing': fileEditingProperties.includes(file.id)}]">
                            <div class="columns">
                                <div class="column" :class="{'is-3': showProperties}" v-if="userCan('update_account_files', 'account_files')">
                                    <div class="field">
                                        <h6 class="title is-6">Options</h6>
                                        <b-checkbox name="sov"
                                                    v-model="files[index].sov"
                                                    v-on:change.native="updateFile($event, file.id)">
                                            <em>Submission Document</em>
                                        </b-checkbox>
                                    </div>
                                    <div class="field" v-if="false">
                                        <b-checkbox name="provided_by_broker"
                                                    v-model="files[index].provided_by_broker"
                                                    v-on:change.native="updateFile($event, file.id)">
                                            <em>Provided by Broker</em>
                                        </b-checkbox>
                                    </div>
                                    <div class="field" v-if="false">
                                        <b-checkbox name="private"
                                                    v-model="files[index].private"
                                                    v-on:change.native="updateFile($event, file.id)">
                                            <em>File is Private</em>
                                        </b-checkbox>
                                    </div>
                                </div>
                                <div class="column is-5">
                                    <h6 class="title is-6">File Data</h6>
                                    <dl>
                                        <dt v-if="!showAddedOn">Added On</dt>
                                        <dd v-if="!showAddedOn">{{ file.created_at }}</dd>
                                        <dt>File Size</dt>
                                        <dd>{{ file.file.size }}</dd>
                                        <dt>{{ showVanityByDefault ? 'Original File Name' : 'Vanity Name' }}</dt>
                                        <dd :class="{'full-name': showFullFileNames}">{{ showVanityByDefault ? file.file.original_file_name : file.vanity_name }}</dd>
                                        <template v-if="file.report_author_date">
                                            <dt>Report Author Date</dt>
                                            <dd>{{ file.report_author_date }}</dd>
                                        </template>
                                    </dl>
                                </div>
                                <div class="column is-4" v-if="showProperties">
                                    <div class="file-associated-properties">
                                        <h6 class="title is-6">
                                            Associated Properties
                                        </h6>
                                        <div>
                                            <b-loading :is-full-page="false" :active.sync="loadingProperties" :can-cancel="false" />
                                            <template v-if="!fileEditingProperties.includes(file.id)">
                                                <custom-scrollbar>
                                                    <ul class="file-associated-properties-list" v-if="file.properties.length > 0">
                                                        <li v-for="(property, index) in file.properties" :key="`acf-property-${index}`">
                                                            <router-link :to="{path: `/accounts/${accountId}/properties/${property.id}/`}">
                                                                {{ property.full_address }}
                                                            </router-link>
                                                        </li>
                                                    </ul>
                                                    <b-message v-else type="is-info">
                                                        <small>No associated properties.</small>
                                                    </b-message>
                                                </custom-scrollbar>
                                            </template>
                                            <b-button :to="{ name: 'account-file', params: { file_id: file.id, id: accountId, sort: sortCol, order: sortDir, search: fileSearch, filters: filters } }"
                                                      tag="router-link"
                                                      type="is-default" size="is-small" class="pull-right edit">
                                                Edit Properties
                                            </b-button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <div class="m-t-10">
            <b-pagination
              v-if="hasFiles && !loading && pagination.total > 15"
              v-on:change="changePage"
              :total="pagination.total"
              :current.sync="pagination.current_page"
              range-before="3"
              range-after="3"
              order="is-centered"
              :per-page="pagination.per_page"
              aria-next-label="Next page"
              aria-previous-label="Previous page"
              aria-page-label="Page"
              aria-current-label="Current page">
            </b-pagination>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapState } from 'vuex'
import { ACCOUNT_FILES_GET, ACCOUNT_FILE_GET, ACCOUNT_FILE_UPDATE, ACCOUNT_FILES_DELETE, ACCOUNT_FILE_ACTION } from '@/store/actions/accounts'
import debounce from 'lodash/debounce'
import generateDocList from '@/functions/generateDocList'
import CustomScrollbar from '../../Elements/CustomScrollbar'
import stringifyObject from '@/functions/stringifyObject'
import getObj from '@/functions/getObj'
import hasObj from '@/functions/hasObj'
import ProgressBar from '@/components/Elements/ProgressBar'

export default {
  name: 'AccountFiles',
  props: {
    showHeader: {
      type: Boolean,
      default: true
    },
    showUploadButton: {
      type: Boolean,
      default: true
    },
    showDelete: {
      type: Boolean,
      default: true
    },
    showFileDetails: {
      type: Boolean,
      default: true
    },
    showProperties: {
      type: Boolean,
      default: true
    },
    showAddedOn: {
      type: Boolean,
      default: true
    },
    filters: {
      type: Object
    },
    paginate: {
      type: Number,
      default: 15
    },
    accountName: {
      type: String,
      default: ''
    },
    showVanityByDefault: {
      type: Boolean,
      default: false
    },
    showFullFileNames: {
      type: Boolean,
      default: false
    }
  },
  components: {
    CustomScrollbar: CustomScrollbar,
    ProgressBar: ProgressBar
  },
  data () {
    return {
      model: 'account_files',
      loading: true,
      loadingPage: false,
      loadingProperties: false,
      accountId: this.$route.params.id,
      propertyId: this.$route.params.prop_id,
      pagination: {
        current_page: 1,
        total: 0
      },
      files: [], // all files on current page
      filesOpen: [], // expanded files
      filesSelected: [], // selected files
      selectedAll: false,
      properties: [], // array of available properties to attach
      fileEditingProperties: [], // tracks which file attached properties are being edited
      generatingDocList: false, // button model
      sortCol: 'created_at',
      sortDir: 'desc'
    }
  },
  computed: {
    ...mapState({
      init: state => state.init.all,
      datatableState: state => state.datatableState,
      datatableIndex: state => state.datatableIndex.models
    }),
    ...mapGetters({
      modelUpdatedAt: 'modelUpdatedAt',
      isAuthenticated: 'isAuthenticated',
      dataChanged: 'dataChanged',
      pusher: 'pusher/init',
      userCan: 'userCan',
      datatableSearch: 'datatableSearch',
      hasSelectionDownloadPending: 'hasSelectionDownloadPending',
      selectionDownloads: 'selectionDownloads',
      userFromShareLink: 'isFromShareLink',
      userFromInviteLink: 'isFromInviteLink'
    }),
    fileSearch: {
      get: function () {
        return this.datatableSearch(this.uniqueModel)
      },
      set: function (newValue) {
        this.$store.commit('updateDatatableStateSearch', {
          'model': this.uniqueModel,
          'search': newValue
        })
      }
    },
    uniqueModel () { // used for state tracking for this specific instance
      let key
      if (this.filters) key = '_' + stringifyObject(this.filters)

      if (this.accountId) return this.model + '_account_id-' + this.accountId + key
      return this.model
    },
    hasFiles () {
      if (this.fileSearch !== '') return true
      return this.files.length > 0
    },
    uploadRoute () {
      if (this.propertyId !== undefined) {
        if (this.$route.path.includes('upload')) {
          return {
            name: 'account-properties',
            query: this.filters,
            params: { 'id': this.accountId, 'prop_id': this.propertyId }
          }
        }

        return {
          name: 'account-properties-upload',
          query: this.filters,
          params: { 'id': this.accountId, 'prop_id': this.propertyId }
        }
      }

      return {
        name: 'account-files',
        query: this.filters,
        params: { 'id': this.accountId }
      }
    },
    pusherChannel: function () {
      if (!this.pusher) return
      return this.pusher.subscribe('private-datatable.account_files')
    },
    hasFileActions () {
      if (this.filesSelected.length > 0) {
        if (this.userCan('download_all_files', 'account_files')) return true
        if (this.userCan('delete_account_files', 'account_files')) return true
      }

      if (this.hasFiles && this.userCan('generate_disclosed_document_list', 'account_files')) return true

      return false
    }
  },
  watch: {
    modelUpdatedAt: function () {
      if (this.dataChanged === 'files') {
        this.getAccountFiles(true, true)
      }
    },
    fileSearch: debounce(function () {
      this.loadingPage = true
      this.getAccountFiles()
    }, 800),
    sortCol: function () {
      this.loadingPage = true
      this.getAccountFiles()
    },
    sortDir: function () {
      this.loadingPage = true
      this.getAccountFiles()
    }
  },
  methods: {
    handleError (err) {
      this.loading = false
      if (err.hasOwnProperty('response')) {
        if (err.response.data.response === 'error') {
          this.$store.commit('modelUpdated', 'files')
          this.$awn.warning(err.response.data.message)
        }
      } else {
        this.$awn.alert(err.toString())
      }
    },
    changePage (page) {
      this.pagination.current_page = page
      this.loadingPage = true
      this.selectedAll = false
      this.getAccountFiles()
    },
    getAccountFiles (force = false, noRefresh = false) {
      let refresh = false
      let loadFromState = false
      let response = {}

      let query = {
        'account_id': this.accountId,
        'page': this.pagination.current_page,
        'search': this.fileSearch,
        'order': this.sortDir,
        'sort': this.sortCol,
        'paginate': this.paginate,
        ...this.filters
      }

      const stateQuery = stringifyObject(query)
      let filterPath = 'account_files.index.' + stateQuery

      if (hasObj(this.datatableIndex, filterPath)) {
        loadFromState = true
        refresh = true
        response = getObj(this.datatableIndex, filterPath)
      }

      if (loadFromState && force === false) {
        this.handleAccountFilesResponse(response)
        refresh = true
        loadFromState = false
      } else {
        this.$store.dispatch(ACCOUNT_FILES_GET, query).then(response => {
          this.handleAccountFilesResponse(response)
        }).catch(err => {
          console.error(err)
          this.handleError(err)
        })
      }

      if (refresh && noRefresh === false) {
        this.$store.dispatch(ACCOUNT_FILES_GET, query).then(response => {
          this.handleAccountFilesResponse(response)
        }).catch(err => {
          console.error(err)
          this.handleError(err)
        })
      }
    },
    handleAccountFilesResponse (response) {
      this.files = response.data
      this.pagination = response.meta
      this.loading = false
      this.loadingPage = false

      // values come in as 1/0
      // these are used for the v-model
      for (let file in this.files) {
        if (!this.files.hasOwnProperty(file)) continue
        this.files[file].sov = !!+this.files[file].sov
        this.files[file].provided_by_broker = !!+this.files[file].provided_by_broker
        this.files[file].private = !!+this.files[file].private
      }
    },
    getAccountFile (file) {
      this.$awn.info('Preparing your file to download, please wait...')

      this.$store.dispatch(ACCOUNT_FILE_GET, {
        'account_id': this.accountId,
        'file_name': file.file.original_file_name,
        'file_id': file.id
      }).then(response => {
        let url = response.data.url
        const link = document.createElement('a')
        link.href = url
        document.body.appendChild(link)
        link.click()
        link.parentNode.removeChild(link)
      }).catch(err => {
        this.handleError(err)
      })
    },
    previewAccountFile (file) {
      this.$emit('previewing-file', file)
      this.$router.push({
        name: 'account-file',
        params: {
          file_id: file.id,
          id: this.accountId,
          sort: this.sortCol,
          order: this.sortDir,
          search: this.fileSearch,
          filters: this.filters
        }
      })
    },
    toggleFileDetails (fileId) {
      if (this.filesOpen.includes(fileId)) {
        this.$delete(this.filesOpen, this.filesOpen.indexOf(fileId))
      } else {
        this.filesOpen.push(fileId)
      }
    },
    selectDefaultAction: function () {
      if (this.selectedAll === false) {
        this.selectNone()
      } else {
        this.selectEveryFile()
      }
    },
    selectNone: function () {
      this.filesSelected = []
      this.selectedAll = false
    },
    selectAll (input) {
      let filesSelected = this.filesSelected

      if (input !== false) {
        this.files.forEach((file, index) => {
          if (!filesSelected.includes(file.id) && this.canSelectFile(file)) {
            filesSelected.push(file.id)
          }
        })
        this.filesSelected = filesSelected
      } else {
        this.filesSelected = []
      }
    },
    selectEveryFile () {
      let filesSelected = this.filesSelected

      if (this.pagination.total <= this.pagination.per_page) {
        this.files.forEach((file, index) => {
          if (!filesSelected.includes(file.id) && this.canSelectFile(file)) {
            filesSelected = filesSelected.concat(file.id)
          }
        })

        this.filesSelected = filesSelected
        this.selectedAll = true

        return
      }

      this.loadingPage = true
      this.$store.dispatch(ACCOUNT_FILES_GET, {
        'account_id': this.accountId,
        'current_page': 1,
        'search': this.fileSearch,
        'paginate': this.pagination.total,
        ...this.filters
      }).then(response => {
        response.data.forEach((file, index) => {
          if (!filesSelected.includes(file.id) && this.canSelectFile(file)) {
            filesSelected = filesSelected.concat(file.id)
          }
        })

        this.filesSelected = filesSelected
        this.selectedAll = true
        this.loadingPage = false
      }).catch(err => {
        this.handleError(err)
      })
    },
    clearSearch () {
      this.loadingPage = true
      this.fileSearch = null
    },
    updateFile (e, fileId) {
      let data = {
        'account_id': this.accountId,
        'file_id': fileId
      }

      data[e.target.name] = e.target.checked

      this.$store.dispatch(ACCOUNT_FILE_UPDATE, data)
        .then(response => {
          this.$awn.success(response.message)
          this.$store.commit('modelUpdated', 'files')
        })
    },
    editFileProperties (fileId) {
      if (this.fileEditingProperties.includes(fileId)) {
        this.$delete(this.fileEditingProperties, this.fileEditingProperties.indexOf(fileId))
      } else {
        this.fileEditingProperties.push(fileId)
      }
    },
    saveFileProperties (index) {
      this.loadingProperties = true
      let data = {
        'account_id': this.accountId,
        'file_id': this.files[index].id,
        'properties': this.files[index].propertyIds
      }

      this.$store.dispatch(ACCOUNT_FILE_UPDATE, data)
        .then(response => {
          this.editFileProperties(this.files[index].id)

          // add properties using ids to files.id.properties
          const properties = this.properties
          let newProperties = []
          this.files[index].properties = []

          // loop through selected property ids and populate file properties object
          this.files[index].propertyIds.forEach(function (propertyId, index) {
            let selectedProperty = {}
            properties.forEach(function (property) {
              if (property.id === propertyId) {
                selectedProperty = property
              }
            })
            newProperties.push(selectedProperty)
          })

          this.files[index].properties = newProperties

          this.$awn.success(response.message)

          this.loadingProperties = false
          this.$store.commit('modelUpdated', 'files')
        })
    },
    deleteRows () {
      let filesSelected = this.filesSelected

      let deleteMessage = 'Are you sure you want to <b>delete</b> this file? This action cannot be undone.'
      if (filesSelected.length > 1) {
        deleteMessage = 'Are you sure you want to <b>delete</b> the selected files? This action cannot be undone.'
      }

      this.$buefy.dialog.confirm({
        title: 'Confirm Deletion',
        message: deleteMessage,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.loadingPage = true
          this.$store.dispatch(ACCOUNT_FILES_DELETE, {
            'account_id': this.accountId,
            'ids': filesSelected
          }).then(response => {
            this.selectedAll = false
            this.filesSelected = this.filesSelected.filter((el) => !filesSelected.includes(el))
            this.$awn.success(response.message)
            this.getAccountFiles()
            this.$store.commit('modelUpdated', 'files')
          }).catch(err => {
            this.loadingPage = false
            let errorMessage = err.response
            if (err.response) {
              errorMessage = err.response.data.message
            }
            this.$awn.alert(errorMessage)
          })
        }
      })
    },
    performAction (action, organized = true) {
      const data = {
        'account_id': this.accountId,
        'params': {
          'action': action,
          'organized': organized,
          'files': this.filesSelected
        }
      }

      this.$store.dispatch(ACCOUNT_FILE_ACTION, data).then(response => {
        if (response.data.status !== 'accessible') {
          this.$store.commit('addPendingDownload', {
            id: response.data.id,
            accountId: response.data.account_id,
            source: 'fileselection',
            progress: _.get(response.data, 'progress', null)
          })

          this.$awn.success(response.message)
        } else {
          const data = {
            account_id: response.data.account_id,
            file_name: response.data.file.original_file_name,
            file_id: response.data.id
          }
          this.$store.dispatch(ACCOUNT_FILE_GET, data).then(response => {
            const url = response.data.url
            const link = document.createElement('a')
            link.href = url
            document.body.appendChild(link)
            link.click()
            link.parentNode.removeChild(link)
          })
        }
      }).catch(err => {
        this.handleError(err)
      })
    },
    generateDocList () {
      this.generatingDocList = true

      this.$store.dispatch(ACCOUNT_FILES_GET, {
        'account_id': this.accountId,
        'paginate': 9999,
        'filter__private': 0,
        'filter__sov': 0,
        'filter__provided_by_broker': 0
      }).then(response => {
        if (response.meta.total === 0) {
          this.generatingDocList = false

          this.$buefy.dialog.alert({
            title: 'Disclosed Document List Error',
            message: 'This account does not have any <strong>disclosed documents</strong> to generate the list from.',
            type: 'is-warning',
            hasIcon: true,
            icon: 'exclamation-triangle',
            iconPack: 'fa'
          })

          return
        }

        generateDocList(response, this.accountName + ' - Document List.docx', 'Disclosed Document List - ' + this.accountName)
      }).catch(err => {
        this.handleError(err)
        this.generatingDocList = false
      }).finally(() => {
        this.generatingDocList = false
      })
    },
    downloadDocList () {
      this.$store.dispatch(ACCOUNT_FILES_GET, {
        'account_id': this.accountId,
        'paginate': 9999,
        'filter__id': this.filesSelected.join(',')
      }).then(response => {
        if (response.meta.total === 0) {
          this.$buefy.dialog.alert({
            title: 'Document List Error',
            message: 'Could not find those documents.',
            type: 'is-warning',
            hasIcon: true,
            icon: 'exclamation-triangle',
            iconPack: 'fa'
          })

          return
        }

        generateDocList(response, this.accountName + ' - Document List.docx')
      }).catch(err => {
        this.handleError(err)
      })
    },
    canSelectFile (file) {
      if (!this.userCan('view_account_files_datatable_actions', 'account_files')) return false // not needed, but just in case
      if (file.accessible_features.delete && this.userCan('delete_account_files', 'account_files')) return true
      if (this.userCan('download_all_files', 'account_files')) return true

      return false
    },
    sort (column) {
      if (this.sortCol === column) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortDir = 'desc'
      }

      this.sortCol = column
    },
    resetSorting () {
      this.sortDir = 'desc'
      this.sortCol = 'created_at'
    },
    getPercentComplete (percentComplete) {
      if (percentComplete === 0) return 1
      if (percentComplete >= 100) return 100

      return percentComplete
    }
  },
  created () {
    this.$store.commit('initDatatableState', this.uniqueModel)
    this.getAccountFiles()
  },
  mounted () {
    this.pusherChannel.bind('model-updated', (data) => {
      let local = new Date(this.modelUpdatedAt)
      let pusher = new Date(data.updatedAt)

      if (pusher.getTime() > local.getTime()) {
        this.getAccountFiles()
      }
    })
  },
  beforeDestroy () {
    if (this.pusher) {
      this.pusher.unsubscribe('private-datatable.account_files')
      this.pusherChannel.unbind('model-updated')
    }
  }
}
</script>

<style lang="scss">
.account-files.no-file-editing {
  padding-left: 0px;

  .file-details-select {
    margin-left: -40px !important;
  }

  .files-header {
    padding-left: 40px !important;
  }

  .files-wrapper {
    padding-left: 40px !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Account/Tabs/AccountFiles.vue